<section class="min-h-screen">
  <!-- Header -->
  <div class="mb-6">
    <h1 class="text-2xl font-bold tracking-tight text-primary-800 dark:text-primary-400">My Profile</h1>
    <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">View and manage your account information</p>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

    <!-- Profile Card -->
    <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <!-- Banner -->
      <div class="h-20 w-full bg-gradient-to-br from-primary-800 to-primary"></div>
      <div class="px-6 pb-6">
        <!-- Avatar -->
        <div class="flex items-end justify-between -mt-8 mb-4">
          <div class="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center text-xl font-bold text-white shadow-md flex-shrink-0 bg-primary-800">{{ userData.user.firstName.charAt(0) }}{{ userData.user.lastName.charAt(0) }}</div>
          <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900 uppercase tracking-wider">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Active
          </span>
        </div>
        <h2 class="text-base font-bold text-slate-900 dark:text-white">{{ userData.user.firstName }} {{ userData.user.lastName }}</h2>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{{ userData.user.roles[0].name || '—' }}</p>

        <div class="mt-5 space-y-3">
          <div class="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
            <svg class="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span>{{ userData.user.email }}</span>
          </div>
          <div class="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
            <svg class="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span>Accra, Ghana</span>
          </div>
          <div class="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
            <svg class="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span>Joined 2024</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Details + Actions -->
    <div class="lg:col-span-2 space-y-5">

      <!-- Account Details -->
      <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div class="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/50">
          <h3 class="text-xs font-bold uppercase tracking-widest text-primary-800 dark:text-primary-400">Account Details</h3>
        </div>
        <div class="p-5 grid grid-cols-2 gap-x-8 gap-y-5">
          <div>
            <p class="text-xs font-semibold text-slate-400 mb-1">Role</p>
            <p class="text-sm font-semibold text-slate-900 dark:text-white">{{ userData.user.roles[0].name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400 mb-1">Account Status</p>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900 uppercase tracking-wider">
              <span class="w-1 h-1 rounded-full bg-emerald-500 inline-block"></span>Active
            </span>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400 mb-1">Last Login</p>
            <p class="text-sm font-semibold text-slate-900 dark:text-white">Today</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400 mb-1">Two-Factor Auth</p>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 uppercase tracking-wider">Disabled</span>
          </div>
        </div>
      </div>

      <!-- Security -->
      <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div class="px-5 py-3.5 border-b border-slate-100 dark:border-slate-700/50">
          <h3 class="text-xs font-bold uppercase tracking-widest text-primary-800 dark:text-primary-400">Security</h3>
        </div>
        <div class="p-5 space-y-3">
          <div class="flex items-center justify-between py-2.5 border-b border-slate-50">
            <div>
              <p class="text-xs font-bold text-slate-800 dark:text-slate-100">Password</p>
              <p class="text-[10px] text-slate-400 mt-0.5">Last changed recently</p>
            </div>
            <button class="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded border transition-all hover:bg-primary-800/5 text-primary-800 dark:text-primary-400 border-primary-800/20">
              Change
            </button>
          </div>
          <div class="flex items-center justify-between py-2.5">
            <div>
              <p class="text-xs font-bold text-slate-800 dark:text-slate-100">Active Sessions</p>
              <p class="text-[10px] text-slate-400 mt-0.5">1 device currently active</p>
            </div>
            <button class="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded border transition-all hover:bg-rose-50 text-rose-600"
              style="border-color: rgba(239,68,68,0.2);">
              Revoke All
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>
